import {
  Breadcrumb,
  BreadcrumbItem,
} from "flowbite-react/components/Breadcrumb";
import React, { Fragment, useCallback } from "react";
import { TBreadCrumbSingle } from "../../../store/breadcrumb";
import { useStore } from "../../../store/store";
import { useNavigate, useLocation } from "@tanstack/react-router";

const BreadCrumb = () => {
  const breadCumObj = useStore((state) => state.breadCrumbObjRoutes);
  const navigate = useNavigate();
  const location = useLocation();

  const innerContent = useCallback(
    (item: TBreadCrumbSingle, index: number) => {
      const handleResetUrl = (item: TBreadCrumbSingle) => {
        if (!item?.path) return; // safely exit if path is undefined/null

        const absolutePath = item.path.startsWith("/")
          ? item.path
          : `/${item.path}`;

        // Check if we're navigating back to a different route
        // (e.g., going back from details to list)
        const currentPath = location.pathname;
        const isNavigatingToDifferentRoute = currentPath !== absolutePath;
        const shouldUseHistoryBack =
          isNavigatingToDifferentRoute &&
          window.history.length > 1 &&
          item.search &&
          Object.keys(item.search).length > 0;

        if (shouldUseHistoryBack && item.search) {
          // Build the URL with search params
          const searchParams = new URLSearchParams();
          Object.entries(item.search).forEach(([key, value]) => {
            if (value) {
              searchParams.set(key, value);
            }
          });
          const searchString = searchParams.toString();
          const newUrl = searchString
            ? `${absolutePath}?${searchString}`
            : absolutePath;

          // Set up a one-time listener for when history.back() completes
          const handlePopState = () => {
            // Update the URL with search params
            window.history.replaceState(
              { ...window.history.state },
              "",
              newUrl,
            );
            // Trigger TanStack Router to update
            void navigate({
              to: absolutePath,
              search: item.search,
              replace: true,
            });
            // Remove the listener after use (though once: true should handle this)
            window.removeEventListener("popstate", handlePopState);
          };

          // Add listener before going back
          window.addEventListener("popstate", handlePopState, { once: true });

          // Go back one step in history (removes current entry - details screen)
          window.history.back();
        } else {
          // Normal navigation
          void navigate({
            to: absolutePath,
            search: item.search,
            replace: false,
          });
        }
      };

      return (
        <Fragment key={index}>
          {breadCumObj?.length - 1 !== index ? (
            <BreadcrumbItem
              className="cursor-pointer"
              onClick={() => {
                handleResetUrl(item);
              }}
            >
              {item.name}
            </BreadcrumbItem>
          ) : (
            <BreadcrumbItem className="breadcrumb-last-item">
              {item.name}
            </BreadcrumbItem>
          )}
        </Fragment>
      );
    },
    [breadCumObj?.length, navigate, location.pathname],
  );
  return (
    <section className="flex items-center gap-2 text-sm">
      <Breadcrumb>
        {breadCumObj &&
          breadCumObj?.length > 0 &&
          breadCumObj?.map((item, index) => innerContent(item, index))}
      </Breadcrumb>
    </section>
  );
};

export default React.memo(BreadCrumb);
